import { getCore } from '../../../../../client/core/getCore';
import type { DynamicClient } from '../../../../../client/types';
import { NoNetworkProvidersError } from '../../../../../errors/NoNetworkProvidersError';
import { assertDefined } from '../../../../../utils/assertDefined';
import { getWalletProviderByKey } from '../../../getWalletProviderByKey';
import { getNetworkProviders } from '../../getNetworkProviders';
import type { LastKnownNetworkRegistry } from '../LastKnownNetworkRegistry.types';
import { networkRegistryStorageKeySchema } from './schema';

/**
 * Creates the registry that keeps tabs on which wallet provider is
 * connected to which network.
 *
 * Reminder that this will not be used for chains that have an internal way to
 * switch networks (ex EVM).
 */
export const createLastKnownNetworkRegistry = (
  client: DynamicClient
): LastKnownNetworkRegistry => {
  const core = getCore(client);

  const walletProviderKeyToNetworkId = new Map<string, string>();

  const getNetworkId: LastKnownNetworkRegistry['getNetworkId'] = async ({
    walletProviderKey,
  }) => {
    // Priorities:
    // 1. In memory
    // 2. Storage
    // 3. First available network for this wallet's chain

    if (walletProviderKeyToNetworkId.has(walletProviderKey)) {
      return {
        networkId: walletProviderKeyToNetworkId.get(
          walletProviderKey
        ) as string,
      };
    }

    const storageRecords = await core.storage.getItem(
      networkRegistryStorageKeySchema
    );

    if (storageRecords && storageRecords[walletProviderKey]) {
      const networkId = storageRecords[walletProviderKey];

      walletProviderKeyToNetworkId.set(walletProviderKey, networkId);

      return { networkId };
    }

    const walletProvider = getWalletProviderByKey(
      { walletProviderKey },
      client
    );

    const networkProviders = getNetworkProviders(client);

    if (networkProviders.length === 0) {
      throw new NoNetworkProvidersError();
    }

    const defaultNetworkProvider = networkProviders.find(
      (networkProvider) => networkProvider.chain === walletProvider.chain
    );

    assertDefined(
      defaultNetworkProvider,
      `No networks found for chain ${walletProvider.chain}`
    );

    walletProviderKeyToNetworkId.set(
      walletProviderKey,
      defaultNetworkProvider.networkId
    );

    // No need to set in storage, as we're just falling back to the default
    return { networkId: defaultNetworkProvider.networkId };
  };

  const setNetworkId: LastKnownNetworkRegistry['setNetworkId'] = async ({
    networkId,
    walletProviderKey,
  }) => {
    walletProviderKeyToNetworkId.set(walletProviderKey, networkId);

    await core.storage.setItem(
      networkRegistryStorageKeySchema,
      Object.fromEntries(walletProviderKeyToNetworkId.entries())
    );
  };

  return { getNetworkId, setNetworkId };
};
